import type React from 'react'

export function cx(...parts: (string | false | undefined | null)[]): string {
  return parts.filter(Boolean).join(' ')
}

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <section
      className={cx(
        'overflow-hidden rounded-xl border border-line bg-surface',
        // min-w-0 is load-bearing, not tidiness.
        //
        // A grid or flex item defaults to `min-width: auto`, so it refuses to
        // shrink below the width of its own content. A card holding a wide
        // table therefore grew to 454px inside a 320px phone, and the
        // overflow-x-auto wrapper inside it never got the chance to clip — so
        // the page scrolled sideways instead of the table.
        'min-w-0',
        className,
      )}
    >
      {children}
    </section>
  )
}

export function CardHeader({
  title,
  hint,
  action,
}: {
  title: string
  hint?: string
  action?: React.ReactNode
}) {
  return (
    <header className="flex flex-wrap items-start justify-between gap-3 border-b border-line-soft px-5 py-5">
      <div className="min-w-0">
        <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
        {hint ? (
          <p className="mt-1.5 max-w-[65ch] text-xs leading-relaxed text-ink-faint">{hint}</p>
        ) : null}
      </div>
      {action}
    </header>
  )
}

/**
 * A headline number.
 *
 * `note` exists because a figure without its basis invites the wrong
 * conclusion: "median" and "mean" differ by thousands on a salary
 * distribution, and a dashboard that does not say which is which is worse than
 * one that shows neither.
 */
export function Statistic({
  label,
  value,
  valueTitle,
  note,
  tone = 'neutral',
}: {
  label: string
  value: string
  valueTitle?: string
  note?: string
  tone?: 'neutral' | 'low' | 'high' | 'good'
}) {
  const tones = {
    neutral: 'text-ink',
    low: 'text-low',
    high: 'text-high',
    good: 'text-good',
  } as const

  return (
    <div className="min-w-0 px-5 py-5">
      <p className="text-xs font-medium text-ink-faint">{label}</p>
      {/*
        The figure scales with the viewport rather than overflowing it. A
        payroll total like "$653,597,438" is about 160px at text-2xl and cannot
        shrink, so two side by side did not fit a 320px phone — and because a
        grid item will not go below its own content width, the page scrolled
        sideways instead of the number wrapping.
      */}
      <p
        title={valueTitle}
        aria-label={valueTitle}
        className={cx(
          'stat-value mt-3 text-xl font-medium tracking-tight sm:text-[26px]',
          tones[tone],
        )}
      >
        {value}
      </p>
      {note ? <p className="mt-2 text-xs leading-relaxed text-ink-faint">{note}</p> : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------

type ButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
  size?: 'sm' | 'md'
}

export function Button({ variant = 'secondary', size = 'md', className, ...props }: ButtonProps) {
  const variants = {
    primary: 'bg-accent text-white hover:bg-accent/90 disabled:bg-accent/50',
    secondary: 'border border-line bg-surface hover:bg-line-soft disabled:text-ink-faint',
    ghost: 'hover:bg-line-soft disabled:text-ink-faint',
    danger: 'border border-low/30 text-low hover:bg-low-soft',
  } as const

  return (
    <button
      type="button"
      className={cx(
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium transition-colors',
        'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent',
        'disabled:cursor-not-allowed',
        // Thumb-sized on every device, for the reason in `controlClasses`:
        // a breakpoint cannot tell a finger from a mouse, and an iPad is a
        // touch screen wider than Tailwind's `sm`.
        size === 'sm' ? 'min-h-10 px-3 text-xs' : 'min-h-10 px-4 text-sm',
        variants[variant],
        className,
      )}
      {...props}
    />
  )
}

export function Field({
  label,
  hint,
  error,
  children,
  className,
}: {
  label: string
  hint?: string
  error?: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <label className={cx('block', className)}>
      <span className="block text-xs font-medium text-ink-soft">{label}</span>
      {children}
      {error ? (
        <span className="mt-1 block text-xs text-low">{error}</span>
      ) : hint ? (
        <span className="mt-1 block text-xs text-ink-faint">{hint}</span>
      ) : null}
    </label>
  )
}

/*
 * One size for every device, rather than a size per breakpoint.
 *
 * 16px is not a taste decision: Safari on iOS zooms the whole page when a
 * field with a smaller font takes focus, and leaves it zoomed. Every form here
 * was doing that.
 *
 * The first attempt made it 16px only below Tailwind's `sm`, which is a
 * viewport width — and a 768px iPad is a touch device *above* that width, so it
 * went straight back to a 26px control with a 14px font and zoomed on focus
 * exactly as before. Sizing a control by how wide the screen is answers the
 * wrong question; what matters is whether a finger or a mouse is pointing at
 * it, and CSS cannot tell me that in a way this toolchain supports.
 *
 * So: one size that is right for a thumb and unremarkable with a mouse. 40px
 * tall and 16px type is roughly what GitHub, Stripe and Linear ship on the
 * desktop anyway, and it removes a whole class of breakpoint bug.
 */
const controlClasses =
  'mt-1.5 w-full min-h-11 rounded-lg border border-line bg-surface px-3 py-2 text-base transition-colors ' +
  'focus:border-accent focus:outline-2 focus:outline-offset-0 focus:outline-accent/20 ' +
  'disabled:bg-line-soft disabled:text-ink-faint'

export function Input({ className, ...props }: React.InputHTMLAttributes<HTMLInputElement>) {
  return <input className={cx(controlClasses, 'tabular', className)} {...props} />
}

export function Select({
  className,
  children,
  ...props
}: React.SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select className={cx(controlClasses, className)} {...props}>
      {children}
    </select>
  )
}

export function Textarea({
  className,
  ...props
}: React.TextareaHTMLAttributes<HTMLTextAreaElement>) {
  // text-base last, so a caller passing a smaller size cannot reintroduce the
  // iOS zoom-on-focus this is sized to avoid.
  return <textarea className={cx(controlClasses, className, 'text-base')} rows={2} {...props} />
}

// ---------------------------------------------------------------------------
// Signals
// ---------------------------------------------------------------------------

export function Badge({
  children,
  tone = 'neutral',
  title,
}: {
  children: React.ReactNode
  tone?: 'neutral' | 'low' | 'high' | 'good' | 'accent' | 'muted'
  title?: string
}) {
  const tones = {
    neutral: 'bg-line-soft text-ink-soft',
    muted: 'bg-line-soft text-ink-faint',
    low: 'bg-low-soft text-low',
    high: 'bg-high-soft text-high',
    good: 'bg-good-soft text-good',
    accent: 'bg-accent-soft text-accent',
  } as const

  return (
    <span
      title={title}
      className={cx(
        'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium whitespace-nowrap',
        tones[tone],
      )}
    >
      {children}
    </span>
  )
}

/**
 * Where a salary sits against its band.
 *
 * Above maximum is amber rather than red on purpose. It is a budget question
 * and often a legitimate one — somebody retained through a hot market — whereas
 * below minimum is a retention risk and usually an error. Giving them the same
 * colour would say they need the same response.
 */
export function BandBadge({ position }: { position?: string }) {
  if (!position)
    return (
      <Badge tone="muted" title="No range published for this role">
        —
      </Badge>
    )

  if (position === 'BELOW_MINIMUM') return <Badge tone="low">Below range</Badge>
  if (position === 'ABOVE_MAXIMUM') return <Badge tone="high">Above range</Badge>
  return <Badge tone="good">In range</Badge>
}

export function ReasonBadge({ reason }: { reason: string }) {
  const tone = reason === 'PROMOTION' ? 'accent' : reason === 'CORRECTION' ? 'high' : 'neutral'
  return <Badge tone={tone}>{reason.replace(/_/g, ' ').toLowerCase()}</Badge>
}

// ---------------------------------------------------------------------------
// States
//
// Three distinct ones, deliberately. "Loading", "nothing matched" and "it
// broke" need different words and different next actions, and collapsing them
// into one spinner-or-blank is how an interface stops telling you anything.
// ---------------------------------------------------------------------------

export function Skeleton({ className }: { className?: string }) {
  return <div className={cx('animate-pulse rounded bg-line-soft', className)} />
}

export function Empty({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="px-6 py-16 text-center">
      <p className="text-sm font-medium text-ink-soft">{title}</p>
      {hint ? (
        <p className="mx-auto mt-2 max-w-md text-xs leading-relaxed text-ink-faint">{hint}</p>
      ) : null}
    </div>
  )
}

/*
 * `onRetry` returns whatever it likes, including a promise.
 *
 * Every caller passes a query's `refetch`, which is promise-returning, and
 * this component does not care when or whether it finishes — the query renders
 * its own next state. Typing the prop as `() => void` and handing it a promise
 * is the mistake the linter is named after, so the type says what is true and
 * the result is discarded explicitly, in one place rather than eight.
 */
export function Failure({
  error,
  onRetry,
}: {
  error: unknown
  onRetry?: () => void | Promise<unknown>
}) {
  const message = error instanceof Error ? error.message : 'Something went wrong'

  return (
    <div className="px-4 py-8 text-center">
      <p className="text-sm font-medium text-low">{message}</p>
      {onRetry ? (
        <Button className="mt-3" onClick={() => void onRetry()}>
          Try again
        </Button>
      ) : null}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

export function Table({ children }: { children: React.ReactNode }) {
  return (
    // max-w-full and min-w-0 so this is what scrolls rather than the page. A
    // nine-column table genuinely does not fit a phone, and scrolling within
    // the table is the honest answer — dropping columns would withhold the
    // figures somebody came for.
    // tabIndex makes the scrollable region reachable without a pointer. A
    // region that scrolls and cannot be focused is unreachable by keyboard —
    // the columns past the right edge simply do not exist for somebody not
    // using a mouse, which is the accessibility cost of the decision above and
    // is worth one attribute rather than a trade.
    <div className="min-w-0 max-w-full overflow-x-auto overscroll-x-contain" tabIndex={0}>
      <table className="data-table w-full border-collapse text-sm">{children}</table>
    </div>
  )
}

export function Th({
  children,
  align = 'left',
  className,
  onClick,
  sorted,
  hint,
}: {
  children?: React.ReactNode
  align?: 'left' | 'right' | 'center'
  className?: string
  onClick?: () => void
  sorted?: 'asc' | 'desc'
  /**
   * A few words under the column name, for the ones whose name is not enough:
   * "100% = midpoint" under "Vs midpoint".
   *
   * Visible rather than a tooltip, on purpose. A `title` attribute does not
   * exist for anybody on a touchscreen and a custom tooltip has to be
   * dismissible, hoverable and persistent to satisfy WCAG 1.4.13 — a lot of
   * machinery to hide four words that fit on the screen. Anything longer than
   * a few words belongs in the sentence above the table, not here.
   */
  hint?: string
}) {
  const content = (
    <>
      {children}
      {sorted ? (
        <span aria-hidden className="ml-1 text-ink-faint">
          {sorted === 'asc' ? '↑' : '↓'}
        </span>
      ) : null}
    </>
  )

  return (
    <th
      scope="col"
      aria-sort={sorted ? (sorted === 'asc' ? 'ascending' : 'descending') : undefined}
      className={cx(
        'border-b border-line bg-canvas/70 px-5 py-3 text-xs font-medium text-ink-soft whitespace-nowrap',
        align === 'right' && 'text-right',
        align === 'center' && 'text-center',
        className,
      )}
    >
      {onClick ? (
        <button
          type="button"
          onClick={onClick}
          // Tall enough to hit. These were 16px, inside a table that scrolls
          // sideways on a phone, which made sorting the roster a lottery.
          className="-my-1 inline-flex min-h-10 items-center rounded px-0.5 hover:text-ink focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
        >
          {content}
        </button>
      ) : (
        content
      )}
      {hint ? <span className="mt-0.5 block font-normal text-ink-faint">{hint}</span> : null}
    </th>
  )
}

export function Td({
  children,
  align = 'left',
  className,
  colSpan,
}: {
  children?: React.ReactNode
  align?: 'left' | 'right' | 'center'
  className?: string
  colSpan?: number
}) {
  return (
    <td
      colSpan={colSpan}
      className={cx(
        'border-b border-line-soft px-5 py-3.5',
        align === 'right' && 'tabular text-right',
        align === 'center' && 'text-center',
        className,
      )}
    >
      {children}
    </td>
  )
}

export function Pager({
  page,
  totalPages,
  totalItems,
  onChange,
}: {
  page: number
  totalPages: number
  totalItems: number
  onChange: (page: number) => void
}) {
  return (
    <div className="flex flex-wrap items-center justify-between gap-2 border-t border-line-soft px-5 py-4 text-xs text-ink-soft">
      <span className="tabular">
        Page {page.toLocaleString()} of {totalPages.toLocaleString()} ·{' '}
        {totalItems.toLocaleString()} {totalItems === 1 ? 'record' : 'records'}
      </span>
      <div className="flex gap-1.5">
        <Button size="sm" disabled={page <= 1} onClick={() => onChange(page - 1)}>
          Previous
        </Button>
        <Button size="sm" disabled={page >= totalPages} onClick={() => onChange(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  )
}

export function PageHeader({
  title,
  description,
  action,
}: {
  title: string
  description: string
  action?: React.ReactNode
}) {
  return (
    <header className="page-heading">
      <div>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
      {action ? <div className="page-heading-action">{action}</div> : null}
    </header>
  )
}
